<template>
  <section
    :class="{'is-safari': isSafari}"
    class="monitor-dashboard">
    <article ref="blank" class="blank-screen">
    </article>
    <article class="content-container">
      <div class="heading-container">
        <div class="text-container">
          <img
            :src="byteArkLogo"
            class="text-icon"
            alt="ByteArk Signal">
          <h1 class="header-title">ByteArk Signal</h1>
          <div
            :class="{'is-live': isStreaming}"
            class="live-status">
            <img
              :src="liveStartedIcon"
              v-if="isStreaming"
              class="channel-icon"
              alt="Live">
            <img
              :src="liveNotStartedIcon"
              v-if="!isStreaming"
              class="channel-icon"
              alt="Live not started yet">
            <h4 v-if="isStreaming" class="status-title">{{$t('dashboard.isLive')}} {{streamName}}</h4>
            <h4 v-if="!isStreaming" class="status-title">{{$t('dashboard.isNotLive')}}</h4>
          </div>
        </div>
      </div>
      <div class="monitor-container">
        <div class="chart-and-status-container">
          <div
            :class="{
              'is-safari': isSafari,
              'no-chart': shouldHideChart,
            }"
            class="chart-container">
            <LineChart />
            <div
              v-if="latestUpdate"
              class="chart-toolbar">
              <div class="chart-last-update">
                <p class="status-text stream--last-update">
                  <template v-if="language === 'th'">
                    {{$t('dashboard.lastUpdated')}} {{latestUpdate}} ({{$t('dashboard.updateInterval')}})
                  </template>
                  <template v-else>
                    {{$t('dashboard.lastUpdated')}} {{latestUpdate}} ({{getTimezone()}} | {{$t('dashboard.updateInterval')}})
                  </template>
                </p>
              </div>
              <div
                ref="sliderZoomContainer"
                class="timeline-zoom-container">
                <div class="zoom-text-hint">
                  <p class="zoom-size">{{$t('zoomRange')}}: {{getZoomSize()}}</p>
                </div>
                <div class="zoom-slider">
                  <div class="slider-icon">
                    <img
                      :src="zoomOutIcon"
                      alt="Zoom Out"
                      class="zoom-out-icon"
                      @click.stop="zoomOut()">
                  </div>
                  <div
                    ref="sliderTrack"
                    class="slider-track">
                    <div
                      ref="sliderButton"
                      class="current-point" />
                    <div
                      class="slider-point"
                      @click.stop="setInterval(0)" />
                    <div
                      class="slider-point"
                      @click.stop="setInterval(60)" />
                    <div
                      class="slider-point"
                      @click.stop="setInterval(140)" />
                    <div
                      class="slider-point"
                      @click.stop="setInterval(200)" />
                  </div>
                  <div class="slider-icon">
                    <img
                      :src="zoomInIcon"
                      alt="Zoom In"
                      class="zoom-in-icon"
                      @click.stop="zoomIn()">
                  </div>
                </div>
              </div>
            </div>
            <p
              v-if="!latestUpdate"
              class="status-text stream--no-last-update">
              {{$t('dashboard.noPlotData')}}
            </p>
          </div>
          <div
            ref="statusContainer"
            :class="{'three-cards': !showingCard.stream}"
            class="status-container">
            <div
              ref="streamInfo"
              class="status-card status--channel">
              <p
                class="hide-card-button"
                @click.stop="onHideStreamingInfo">×</p>
              <h4 class="status--title">Streaming Information</h4>
              <p class="status-text channel--title">Channel Name: {{streamName}}</p>
              <div class="general-info-container">
                <div class="general-info--server">
                  <p class="status-text">Server: {{streamServer}}</p>
                  <div
                    v-if="!isServerCopied"
                    class="copy-icon-container">
                    <img
                      class="copy-icon"
                      @click.stop="copyValue('streamServer')"
                      :src="copyIcon"
                      alt="copy">
                  </div>
                  <span class="is-copied" v-if="isServerCopied">Copied!</span>
                </div>
                <div class="general-info--stream-key">
                  <p class="status-text">
                    Stream Key:
                    <span class="stream-key">
                  <template v-for="(iterator, index) in 14">
                    <template v-if="iterator % 5 === 0">
                      <span class="stream-key-separator" :key="index">-</span>
                    </template>
                    <template v-else>
                      <img
                        class="stream-key-concealer"
                        :src="dotIcon"
                        :key="index"
                        alt="dot">
                    </template>
                  </template>
                </span>
                  </p>
                  <div
                    v-if="!isStreamKeyCopied"
                    class="copy-icon-container">
                    <img
                      class="copy-icon"
                      @click.stop="copyValue('streamKey')"
                      :src="copyIcon"
                      alt="copy">
                  </div>
                  <span class="is-copied" v-if="isStreamKeyCopied">Copied!</span>
                </div>
              </div>
            </div>
            <div
              :class="{'error': !videoBitrateObject}"
              class="status-card status--video">
              <h4 class="status--title">Video</h4>
              <template v-if="videoBitrateObject">
                <p class="status-text video--resolution">
                  <span class="status-key">Resolution</span><span v-html="videoResolution"></span>
                </p>
                <p class="status-text video--frame-rate">
                  <span class="status-key">Frame Rate</span>
                  <template v-if="videoFrameRate"> {{videoFrameRate}}</template>
                  <template v-if="!videoFrameRate">{{$t('dashboard.probeError')}}</template>
                </p>
                <p class="status-text video--codec"><span class="status-key">Codec</span><span v-html="videoCodec"></span></p>
                <p class="status-text video--bitrate">
                  <span class="status-key">Streaming Bitrate</span>
                  <template v-if="videoBitrate"> {{Math.round(videoBitrate)}} kbps</template>
                  <template v-if="!videoBitrate"> {{$t('dashboard.probeError')}}</template>
                </p>
                <div
                  ref="advancedVideo"
                  class="advanced-stats">
                  <p class="status-text video--advanced">
                    <span class="status-key">Max Bitrate</span><span>{{videoStat.max}}</span>
                  </p>
                  <p class="status-text video--advanced">
                    <span class="status-key">Average Bitrate</span><span>{{videoStat.avg}}</span>
                  </p>
                  <p class="status-text video--advanced">
                    <span class="status-key">Min Bitrate</span><span>{{videoStat.min}}</span>
                  </p>
                </div>
              </template>
              <template v-if="!videoBitrateObject">
                <p class="status-text video--codec">{{$t('dashboard.noVideoSignal')}}</p>
              </template>
            </div>
            <div
              :class="{'error': !audioBitrateObject}"
              class="status-card status--audio">
              <h4 class="status--title">Audio</h4>
              <template v-if="audioBitrateObject">
                <div
                  v-for="(track, index) in audioTracks"
                  :key="index"
                  class="audio-track">
                  <h5
                    v-if="audioTracks.length > 1"
                    class="audio-track-title">Track {{index + 1}}</h5>
                  <p class="status-text audio--codec full-name"><span class="status-key">Codec</span> {{track.codec_long_name || $t('dashboard.probeError')}}</p>
                  <p class="status-text audio--codec short-name"><span class="status-key">Codec</span> {{(track.codec_name || '').toUpperCase() || $t('dashboard.probeError')}}</p>
                  <p class="status-text audio--profile"><span class="status-key">Profile</span> {{track.profile || $t('dashboard.probeError')}}</p>
                  <p class="status-text audio--sample-rate"><span class="status-key">Sample Rate</span> {{track.sample_rate || $t('dashboard.probeError')}}</p>
                  <p class="status-text audio--channels">
                    <span class="status-key">Channels</span>
                    <span v-if="track.channels">{{track.channels}} (<span style="text-transform: capitalize">{{track.channel_layout}}</span>)</span>
                    <span v-else>{{$t('dashboard.probeError')}}</span>
                  </p>
                  <p class="status-text audio--bitrate">
                    <span class="status-key">Streaming Bitrate</span>
                    <template v-if="track.bit_rate"> {{Math.round(bitToKbps(track.bit_rate))}} kbps</template>
                    <template v-else> {{$t('dashboard.probeError')}}</template>
                  </p>
                </div>
                <div
                  ref="advancedAudio"
                  class="advanced-stats">
                  <p class="status-text audio--advanced">
                    <span class="status-key">Max Bitrate</span><span>{{audioStat.max}}</span>
                  </p>
                  <p class="status-text audio--advanced">
                    <span class="status-key">Average Bitrate</span><span>{{audioStat.avg}}</span>
                  </p>
                  <p class="status-text audio--advanced">
                    <span class="status-key">Min Bitrate</span><span>{{audioStat.min}}</span>
                  </p>
                </div>
              </template>
              <template v-if="!audioBitrateObject">
                <p class="status-text audio--codec">{{$t('dashboard.noAudioSignal')}}</p>
              </template>
            </div>
            <div class="status-card status--stream">
              <h4 class="status--title">Transcoding Status</h4>
              <div class="error-container">
                <p
                  v-if="!videoBitrateObject"
                  class="status-text stream--error">
                  &#9888; {{$t('dashboard.noVideoSignalFull')}}
                </p>
                <p
                  v-if="!audioBitrateObject"
                  class="status-text stream--error">
                  &#9888; {{$t('dashboard.noAudioSignalFull')}}
                </p>
                <template v-if="streamErrors">
                  <div class="error-container">
                    <p
                      class="status-text stream--error"
                      v-for="(error, index) in streamErrors"
                      :key="index">
                      &#9888; {{error}}
                    </p>
                  </div>
                </template>
              </div>
              <div class="status-by-resolution">
                <p
                  v-for="(status, resolution, index) in resolutions"
                  :key="index"
                  :class="{
                      'normal': status === 1,
                      'no-resolution': status === -1,
                    }"
                  class="resolution-status-text">
                  <span class="resolution-short-name">{{resolution}}</span>
                  <span class="resolution-status">{{getResolutionStatus(status)}}</span>
                </p>
              </div>
            </div>
            <div class="version-container">
              <p class="version">Version 1.2.3</p>
            </div>
          </div>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import LineChart from '@/components/LineChart.vue';
import bitToKbps from '@/utils/unitConverter';
import { mapState } from 'vuex';

const byteArkLogo = require('@/assets/images/byteark-logo-monotone.svg');
const liveStartedIcon = require('@/assets/images/icons/live-started.svg');
const liveNotStartedIcon = require('@/assets/images/icons/live-not-started.svg');
const copyIcon = require('@/assets/images/icons/copy.svg');
const dotIcon = require('@/assets/images/icons/dot.svg');
const statusIcon = require('@/assets/images/icons/status.svg');
const zoomInIcon = require('@/assets/images/icons/zoom-in.svg');
const zoomOutIcon = require('@/assets/images/icons/zoom-out.svg');

export default {
  data() {
    return {
      isServerCopied: false,
      isStreamKeyCopied: false,
      byteArkLogo,
      copyIcon,
      dotIcon,
      liveStartedIcon,
      liveNotStartedIcon,
      statusIcon,
      isZoomDragging: false,
      zoomInIcon,
      zoomOutIcon,
      showingCard: {
        stream: true,
        video: true,
        audio: true,
        status: true,
      },
    };
  },
  computed: {
    ...mapState({
      stream: (state) => state.stream,
      language: (state) => state.language,
      interval: (state) => state.chartInterval,
      videoStat: (state) => state.currentData.videoBitrate,
      audioStat: (state) => state.currentData.audioBitrate,
    }),
    audioBitrateObject() {
      if (this.stream) {
        try {
          return this.stream.source.audio.bitrates;
        } catch (error) {
          return null;
        }
      }
      return this.$t('dashboard.probeError');
    },
    audioTracks() {
      if (!this.stream) return [];
      try {
        const { audio } = this.stream.probe;
        if (Array.isArray(audio)) return audio;
        if (audio) return [audio];
        return [];
      } catch (e) {
        return [];
      }
    },
    currentAudioBitrate() {
      if (this.audioBitrateObject) {
        return bitToKbps(this.audioBitrateObject[this.stream.lastUpdated], true);
      }
      return null;
    },
    currentVideoBitrate() {
      if (this.videoBitrateObject) {
        return bitToKbps(this.videoBitrateObject[this.stream.lastUpdated], true);
      }
      return null;
    },
    latestUpdate() {
      if (this.stream) {
        if (this.stream.lastUpdated === 0) {
          return false;
        }
        const date = new Date(this.stream.source.lastupdated * 1000);
        const hour = date.getHours();
        let minute = date.getMinutes();
        let second = date.getSeconds();
        if (minute < 10) {
          minute = `0${minute}`;
        }
        if (second < 10) {
          second = `0${second}`;
        }
        return `${hour}:${minute}:${second}`;
      }
      return '-';
    },
    hasVideo() {
      if (this.stream) {
        return this.stream.probe.video.codec_name.length > 0;
      }
      return false;
    },
    isSafari() {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.indexOf('safari') !== -1) {
        if (userAgent.indexOf('chrome') > -1) {
          return false; // Chrome
        }
        return true; // Safari
      }
      return false;
    },
    isStreaming() {
      if (this.stream) {
        return this.stream.publishstatus;
      }
      return false;
    },
    streamErrors() {
      if (this.stream) {
        return this.stream.notices;
      }
      return false;
    },
    resolutions() {
      if (this.stream) {
        return this.stream.transcode;
      }
      return null;
    },
    shouldHideChart() {
      return !this.videoBitrateObject;
    },
    streamName() {
      if (this.stream) {
        return this.stream.streamname;
      }
      return null;
    },
    streamServer() {
      if (this.stream) {
        return this.stream.server;
      }
      return null;
    },
    streamKey() {
      if (this.stream) {
        return this.stream.streamkey;
      }
      return null;
    },
    videoBitrateObject() {
      if (this.stream) {
        try {
          return this.stream.source.video.bitrates;
        } catch (error) {
          return null;
        }
      }
      return this.$t('dashboard.probeError');
    },
    videoResolution() {
      if (this.stream) {
        try {
          const { probe } = this.stream;
          if (probe.video.width) {
            return `${probe.video.width}x${probe.video.height} (<span style="text-transform: capitalize">${probe.video.field_order}</span>)`;
          }
          return this.$t('dashboard.probeError');
        } catch (error) {
          return this.$t('dashboard.probeError');
        }
      }
      return this.$t('dashboard.probeError');
    },
    videoFrameRate() {
      if (this.stream) {
        try {
          const [frame, divider] = this.stream.probe.video.r_frame_rate.split('/');
          const result = frame / divider;
          if (Number.isNaN(result)) {
            return false;
          }
          return Math.round(result * 100) / 100;
        } catch (error) {
          return this.$t('dashboard.probeError');
        }
      }
      return this.$t('dashboard.probeError');
    },
    videoCodec() {
      if (this.stream) {
        const { probe } = this.stream;
        try {
          const level = probe.video.level / 10;
          if (probe.video.codec_name) {
            return `<span style="text-transform: uppercase">${probe.video.codec_name}</span> (${probe.video.profile} @ ${level})`;
          }
          return this.$t('dashboard.probeError');
        } catch (error) {
          return this.$t('dashboard.probeError');
        }
      }
      return this.$t('dashboard.probeError');
    },
    videoBitrate() {
      if (this.stream) {
        try {
          const { probe } = this.stream;
          return probe.video.bit_rate / 1000;
        } catch (error) {
          return this.$t('dashboard.probeError');
        }
      }
      return this.$t('dashboard.probeError');
    },
    shouldShowAdditionalInfo() {
      return this.showingCard.stream === false;
    },
  },
  components: {
    LineChart,
  },
  mounted() {
    setTimeout(() => {
      try {
        this.$refs.blank.style.opacity = '0';
        this.$refs.blank.style.zIndex = '-1';
        this.$refs.sliderZoomContainer.addEventListener('mousedown', this.onSliderButtonMouseDown);
        this.$refs.sliderZoomContainer.addEventListener('mousemove', this.onSliderButtonMouseMove);
        this.$refs.sliderZoomContainer.addEventListener('mouseup', this.onSliderButtonMouseUp);
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }, 800);
  },
  methods: {
    bitToKbps,
    getResolutionStatus(resolution) {
      switch (resolution) {
        case 1:
          return this.$t('dashboard.hasSignal');
        case 0:
          return this.$t('dashboard.noSignal');
        default:
          return this.$t('dashboard.signalUnavailable');
      }
    },
    copyValue(keyName) {
      // @keyName (String) ['streamKey']
      const temporaryField = document.createElement('textarea');
      document.body.appendChild(temporaryField);
      if (keyName === 'streamKey') {
        temporaryField.value = this.streamKey;
        this.isStreamKeyCopied = true;
        setTimeout(() => {
          this.isStreamKeyCopied = false;
        }, 1000);
      } else {
        temporaryField.value = this.streamServer;
        this.isServerCopied = true;
        setTimeout(() => {
          this.isServerCopied = false;
        }, 1000);
      }
      temporaryField.select();
      document.execCommand('copy');
      document.body.removeChild(temporaryField);
    },
    setInterval(interval) {
      this.$store.dispatch('setChartInterval', interval);
      this.moveSliderButton();
    },
    onSliderButtonMouseDown() {
      this.isZoomDragging = true;
    },
    onSliderButtonMouseUp() {
      this.isZoomDragging = false;
      this.$refs.sliderZoomContainer.style.cursor = 'default';
    },
    onSliderButtonMouseMove(event) {
      if (this.isZoomDragging) {
        const sliderTrackPosition = event.clientX - this.$refs.sliderTrack.getBoundingClientRect().x;
        const containerPosition = event.clientX - this.$refs.sliderZoomContainer.getBoundingClientRect().x;
        this.$refs.sliderZoomContainer.style.cursor = 'ew-resize';
        if (sliderTrackPosition > 30) {
          this.$store.dispatch('setChartInterval', 0);
        }
        if (sliderTrackPosition > 50 && sliderTrackPosition < 90) {
          this.$store.dispatch('setChartInterval', 60);
        }
        if (sliderTrackPosition >= 90 && sliderTrackPosition < 160) {
          this.$store.dispatch('setChartInterval', 140);
        }
        if (sliderTrackPosition >= 160 && sliderTrackPosition < 298) {
          this.$store.dispatch('setChartInterval', 200);
        }
        this.moveSliderButton();
        if (containerPosition > 298 || containerPosition < 30) {
          this.isZoomDragging = false;
          this.$refs.sliderZoomContainer.style.cursor = 'default';
        }
      }
    },
    moveSliderButton() {
      const button = this.$refs.sliderButton;
      switch (this.interval) {
        case 0:
          button.style.left = '-4px';
          break;
        case 60:
          button.style.left = '62px';
          break;
        case 140:
          button.style.left = '128px';
          break;
        default:
          button.style.left = '192px';
      }
    },
    getZoomSize() {
      switch (this.interval) {
        case 0:
          return `1 ${this.$t('common:hour', { count: 3600 })}`;
        case 60:
          return `45 ${this.$t('common:minute', { count: 1800 })}`;
        case 140:
          return `25 ${this.$t('common:minute', { count: 900 })}`;
        default:
          return `10 ${this.$t('common:minute', { count: 5 })}`;
      }
    },
    zoomIn() {
      switch (this.interval) {
        case 0:
          this.$store.dispatch('setChartInterval', 60);
          break;
        case 60:
          this.$store.dispatch('setChartInterval', 140);
          break;
        case 140:
          this.$store.dispatch('setChartInterval', 200);
          break;
        default:
          this.$store.dispatch('setChartInterval', 200);
      }
      this.moveSliderButton();
    },
    zoomOut() {
      switch (this.interval) {
        case 200:
          this.$store.dispatch('setChartInterval', 140);
          break;
        case 140:
          this.$store.dispatch('setChartInterval', 60);
          break;
        case 60:
          this.$store.dispatch('setChartInterval', 0);
          break;
        default:
          this.$store.dispatch('setChartInterval', 0);
      }
      this.moveSliderButton();
    },
    getTimezone() {
      let offset = new Date().getTimezoneOffset();
      const sign = offset < 0 ? '+' : '-';
      offset = Math.abs(offset);
      // eslint-disable-next-line no-bitwise
      return `GMT${sign}${offset / 60 | 0}`;
    },
    onHideStreamingInfo() {
      this.$refs.streamInfo.style.transform = 'scale(0)';
      setTimeout(() => {
        this.$refs.statusContainer.style.transform = 'translateY(-138px)';
        this.$refs.advancedVideo.style.height = '72px';
        this.$refs.advancedVideo.style.opacity = '1';
        this.$refs.advancedVideo.style.paddingTop = '8px';
        this.$refs.advancedVideo.style.marginTop = '8px';
        this.$refs.advancedAudio.style.height = '72px';
        this.$refs.advancedAudio.style.opacity = '1';
        this.$refs.advancedAudio.style.paddingTop = '8px';
        this.$refs.advancedAudio.style.marginTop = '8px';
      }, 300);
      this.showingCard.stream = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/variables.scss";

.monitor-dashboard {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: $spacer * 2 $spacer $spacer;
  background: $body-bg-darker;
  margin-bottom: $spacer * 3;
  .blank-screen {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    opacity: 1;
    z-index: 3;
    background: $body-bg-darker;
    transition: all 200ms ease;
  }
  .heading-container {
    .live-status {
      display: flex;
      flex-flow: row;
      width: fit-content;
      padding: 4px 18px;
      justify-content: center;
      align-items: flex-end;
      border-radius: 20px;
      margin-left: $spacer;
      background: $red;
      .channel-icon {
        width: 32px;
        margin-right: 10px;
      }
      .status-title {
        margin: 0;
        line-height: 1.3;
      }
      &.is-live {
        background: $green;
      }
    }
    .text-container {
      display: flex;
      flex-flow: row;
      align-items: center;
      .header-title {
        margin: 0;
        font-size: $h2-font-size;
      }
      .text-icon {
        width: 48px;
        margin-right: 10px;
      }
    }
    &::after {
      display: none;
    }
  }
  .content-container {
    position: relative;
    width: 100%;
    max-width: 94vw;
    .monitor-container {
      display: flex;
      flex-flow: row;
      justify-content: space-between;
      flex-wrap: wrap;
      padding: 20px 0;
      .chart-and-status-container {
        display: flex;
        flex-flow: row;
        justify-content: space-between;
        flex: 0 0 100%;
        .chart-container {
          flex: 0 0 75%;
          text-align: center;
          .chart-toolbar {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex-flow: row;
            .chart-last-update {
              display: flex;
              justify-content: flex-end;
              align-items: flex-end;
              flex: 1;
              .status-text {
                margin-bottom: 0;
                margin-right: 28px;
                margin-top: 8px;
              }
            }
            .timeline-zoom-container {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              flex-flow: column;
              .zoom-text-hint {
                .zoom-size {
                  margin: 0 0 -6px;
                  font-size: 10px;
                }
              }
              .zoom-slider {
                width: 300px;
                display: flex;
                flex-flow: row;
                align-items: center;
                justify-content: center;
                .slider-icon {
                  min-width: 30px;
                  min-height: 30px;
                  max-width: 30px;
                  max-height: 30px;
                  width: 30px;
                  height: 30px;
                }
                .zoom-in-icon, .zoom-out-icon {
                  cursor: pointer;
                  width: 36px;
                  height: 36px;
                  padding: 4px 6px 4px 8px;
                  margin-top: -2px;
                  border-radius: 50%;
                  transition: all 250ms ease;
                  &:hover {
                    background: $gray-600;
                  }
                }
                .slider-track {
                  position: relative;
                  display: flex;
                  justify-content: space-between;
                  flex: 1;
                  margin: 0 20px;
                  height: 2px;
                  background: $body-color;
                  .current-point {
                    position: absolute;
                    background: $body-color;
                    width: 20px;
                    height: 20px;
                    margin-top: -10px;
                    border-radius: 50%;
                    top: 2px;
                    left: 128px;
                  }
                  .slider-point {
                    cursor: pointer;
                    margin-top: -3px;
                    width: 4px;
                    height: 10px;
                    background: #969a9f;
                    &:last-child {
                      margin-right: -2px;
                    }
                  }
                }
              }
            }
          }
        }
        .stream--no-last-update {
          height: 100%;
          position: relative;
          align-items: center;
          justify-content: center;
          display: flex;
          color: #CDCDCD;
        }
        .status-container {
          flex: 0 0 22%;
          margin-top: -52px;
          transition: all 500ms cubic-bezier(.13,.63,0,1);
          .status-card {
            position: relative;
            background: $body-bg;
            padding: 20px;
            border-radius: 20px;
            .hide-card-button {
              position: absolute;
              display: flex;
              justify-content: center;
              align-items: center;
              right: 20px;
              top: 13px;
              padding: 3px;
              border-radius: 50%;
              width: 34px;
              height: 34px;
              font-size: 24px;
              opacity: 0;
              text-align: center;
              cursor: pointer;
              transition: all 250ms ease;
            }
            &.error {
              background-color: $red;
            }
            &.status--channel {
              transition: all 750ms cubic-bezier(.13,.63,0,1);
              &:hover {
                .hide-card-button {
                  opacity: 1;
                  &:hover {
                    background: #636367;
                  }
                }
              }
            }
          }
          .advanced-stats {
            //padding-top: 8px;
            //margin-top: 8px;
            border-top: 1px solid #717171;
            //height: 72px;
            height: 0;
            opacity: 0;
            transition: all 250ms ease;
          }
          .status-text {
            display: flex;
            margin-bottom: 0;
          }
          .status--video, .status--audio, .status--stream {
            margin-top: $spacer;
          }
          .audio-track + .audio-track {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }
          .audio-track-title {
            font-size: 0.85rem;
            color: #e28ff3;
            margin-bottom: 4px;
          }
          .status--stream {
            .status-by-resolution {
              .resolution-status-text {
                margin: 2px 0;
                padding: 2px 0;
                //border-bottom: 2px solid #494950;
                .resolution-short-name {
                  display: inline-block;
                  width: 120px;
                }
                .resolution-status {
                  color: #ff495a;
                }
                &.no-resolution {
                  .resolution-status {
                    color: #6e6e75;
                  }
                }
                &.normal {
                  .resolution-status {
                    color: #FFF;
                  }
                }
                &:first-child {
                  margin-top: 10px;
                }
                &:last-child {
                  border-bottom: none;
                }
              }
            }
          }
          .status-key {
            display: inline-block;
            min-width: 120px;
            width: 120px;
          }
          .version-container {
            display: flex;
            width: 100%;
            justify-content: flex-end;
            align-items: flex-end;
            padding-top: $spacer;
            .version {
              color: $gray-700;
            }
          }
          .error-container {
            max-height: 100px;
            overflow-y: auto;
            .stream--error {
              background-color: $red;
              padding: 4px 4px 3px 8px;
              margin: 2px 0;
            }
          }
        }
      }
    }
  }
}

.general-info-container {
  .general-info--server {
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    align-items: center;
  }
  .general-info--server {
    height: 22px;
  }
  .general-info--stream-key {
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    align-items: center;
    height: 22px;
    .stream-key {
      margin-left: $spacer * 0.5;
      .stream-key-concealer {
        width: 14px;
        margin-left: -2px;
      }
      .stream-key-separator {
        margin-left: 2px;
        margin-right: 4px;
      }
    }
  }
  .copy-icon-container {
    cursor: pointer;
    width: 26px;
    height: 26px;
    padding: 6px;
    border-radius: 50%;
    margin-left: $spacer * 0.75;
    background-color: $body-bg-darker;
    transition: background-color 250ms ease;
    .copy-icon {
      padding: 0 3px 7px 0;
      width: 18px;
    }
    &:hover {
      background-color: $body-bg;
    }
  }
  .is-copied {
    display: inline-block;
    margin-left: $spacer * 0.5;
    color: #38d05b;
  }
}

.full-name {
  display: flex !important;
}
.short-name {
  display: none !important;
}

@media screen and (max-width: 1740px) {
  .monitor-dashboard {
    .content-container {
      .monitor-container {
        .chart-and-status-container {
          .chart-container {
            flex: 0 0 70%;
          }
          .status-container {
            flex: 0 0 28%;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1560px) {
  .general-info-container {
    .general-info--stream-key {
      height: 14px;
      .stream-key {
        .stream-key-concealer {
          width: 12px;
        }
      }
    }
    .copy-icon-container {
      width: 20px;
      height: 20px;
      .copy-icon {
        padding: 0 3px 14px 0;
        width: 12px;
      }
    }
  }
  .status--title {
    font-size: $h5-font-size;
  }
  .status-text, .resolution-status-text, .is-copied {
    font-size: $font-size-base * 0.8;
  }
}

@media screen and (max-width: 1400px) {
  .monitor-dashboard {
    .content-container {
      .monitor-container {
        .chart-and-status-container {
          .status-container {
            .status-card {
              padding: 14px;
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1366px) {
  .monitor-dashboard {
    padding: 16px $spacer $spacer;
    .content-container {
      max-width: 99vw;
      .monitor-container {
        .chart-and-status-container {
          .status-container {
            margin-top: -60px;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1200px) {
  .short-name {
    display: flex !important;
  }
  .full-name {
    display: none !important;
  }
}

@media screen and (max-width: 1070px) {
  .monitor-dashboard {
    .content-container {
      .monitor-container {
        .chart-and-status-container {
          .chart-container {
            flex: 0 0 67%;
          }
          .status-container {
            flex: 0 0 32%;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 992px) {
  .monitor-dashboard {
    .content-container {
      .monitor-container {
        .chart-and-status-container {
          flex-flow: column;
          .chart-container {
            flex: 1 1 100%;
            margin-bottom: 80px;
            &.no-chart {
              flex: 1;
              .stream--no-last-update {
                font-size: $h4-font-size;
                height: auto;
              }
            }
          }
          .status-container {
            display: flex;
            flex-flow: row;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: stretch;
            flex: 1 1 100%;
            .status-key {
              min-width: 100px;
              width: 100px;
            }
            .status-card {
              margin-top: 0;
              padding: 20px;
              flex: 0 0 49%;
              &:nth-child(1), &:nth-child(2) {
                margin-bottom: $spacer;
              }
            }
            &.three-cards {
              transform: translateY(20px) !important;
              .status-card {
                margin-top: 0;
                margin-bottom: 0;
                padding: 20px;
                flex: 0 0 32.5%;
                &.status--video {
                  flex: 0 0 35%;
                }
                &.status--audio {
                  flex: 0 0 28%;
                }
                &.status--channel {
                  display: none;
                }
              }
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 660px) {
  .status-text, .resolution-status-text, .is-copied {
    font-size: $font-size-base;
  }
  .monitor-dashboard {
    .heading-container {
      .text-container {
        .header-title {
          display: none;
        }
      }
    }
    .content-container {
      .monitor-container {
        .chart-and-status-container {
          .stream--no-last-update {
            margin: -32px 0 0;
          }
          .chart-container {
            margin-bottom: 80px;
            &.is-safari {
              max-height: 360px;
              margin-bottom: 0;
              &.no-chart {
                max-height: 130px;
                .graph-container {
                  height: 0;
                  margin-bottom: 0;
                }
                .stream--no-last-update {
                  font-size: $font-size-base;
                  margin: -60px 0 14px;
                  height: 160px;
                }
              }
            }
          }
          .status-container {
            .status-card {
              flex: 0 0 100%;
              margin-bottom: $spacer;
            }
            &.three-cards {
              .status-card {
                flex: 0 0 100%;
                margin-bottom: $spacer;
                &.status--video {
                  flex: 0 0 100%;
                  margin-bottom: $spacer;
                }
                &.status--audio {
                  flex: 0 0 100%;
                  margin-bottom: $spacer;
                }
              }
            }
          }
        }
      }
    }
    &.is-safari {
      padding: 16px 1rem 60px;
    }
  }
  .general-info-container {
    .sta
    .status--channel {
      order: 1;
    }
    .status--stream {
      order: 2;
    }
    .status--video {
      order: 3;
    }
    .status--audio {
      order: 4;
    }
  }
  .homepage-content {
    .content-container {
      .setup-container {
        .keys-container {
          flex-flow: column;
          .form-group {
            flex: 0 0 100%;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .monitor-dashboard {
    .chart-last-update {
      display: none;
    }
    .heading-container {
      .text-container {
        .search-icon, .text-icon {
          width: 38px;
          margin-right: 0;
        }
        .live-status {
          flex: 1;
        }
      }
      &::after {
        display: none;
      }
    }
  }
  .general-info-container {
    .general-info--server {
      height: auto;
      margin-bottom: 4px;
    }
  }
}

@media screen and (max-width: 380px) {
  .general-info-container {
    .general-info--server {
      .status-text {
        width: 100%;
        line-height: 1;
      }
    }
    .general-info--stream-key {
      .status-text {
        width: 100%;
      }
    }
  }
}

</style>
